import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import { Contact, CustomerClient, UtilityClient, WebCompany, WebCustomer, WebLocation, WebSwoDeliveryRequest, LocationClient, WebSettings, WebPriceCalculator } from "../../../models";
import { AlertDialogComponent } from "src/app/components/alert-dialog/alert-dialog.component";
import { Subscription } from "rxjs";
import { LocationListService } from "../../../services/location-list.service";
import { MatDialog } from "@angular/material/dialog";
import { RequestConfirmationDialogComponent } from "./request-confirmation-dialog/request-confirmation-dialog.component";
import { CompanyService, ContactService, CustomerService, UtilityService, SharedService } from "../../../services";
import { ErrorDialogComponent } from "../../../components/error-dialog/error-dialog.component";
import { MakeAPaymentDialogComponent } from "src/app/pages/aps/make-a-payment-dialog/make-a-payment-dialog.component";
import { ErrorLoggingService } from "src/app/services/error-logging.service";
import { CrossFieldErrorMatcher } from "src/app/shared";
import { PendingDeliveryDialogComponent } from "../pending-delivery/pending-delivery-dialog.component";
import { PriceCalculatorComponent } from "src/app/components/price-calculator/price-calculator.component";
import { SessionDataService } from "src/app/services/session-data.service";
import { ProcessingDialogComponent } from "../../../components/processing-dialog/processing-dialog.component";
import { NgForm } from "@angular/forms";
import { filter } from "rxjs/operators";

@Component({
    selector: "app-request-form",
    templateUrl: "./request-form.component.html",
    styleUrls: ["./request-form.component.scss"],
})
export class RequestFormComponent implements OnInit, OnDestroy
{

    originalRequestData: WebSwoDeliveryRequest = {};

    isEditEmailEnabled: boolean;
    defaultEmail: string;
    defLoc: WebLocation;
    selectedLoc: WebLocation;
    emailRequest: WebSwoDeliveryRequest = {};
    contact: Contact;
    restrictDeliveryForPayment: boolean;
    paymentRequiredMessage: string = "";
    isPriceCalculatorEnabled: boolean;
    isCODPayBeforeDeliveryEnabled: boolean;
    areDeliveryAmountsLockedIn: boolean;
    messageForCODDelivery: string = "";
    isCODCustomer: boolean;
    isErrorPresentInPriceCalculator: boolean = false;
    isEmployee: boolean = false;

    webPriceCalculator: WebPriceCalculator;

    tankLevel: number;
    requestData: WebSwoDeliveryRequest = { ...this.originalRequestData };

    @Input() isDelivery: boolean;
    @Input() showCustHeader;
    @Output() showCustHeaderChange = new EventEmitter<boolean>();
    @ViewChild('form') form: NgForm;
    @ViewChild(PriceCalculatorComponent)
    priceCalculatorComponent?: PriceCalculatorComponent;


    companySubscription: Subscription;
    company: WebCompany;

    customerSubscription: Subscription;
    customer: WebCustomer;
    customerClient: CustomerClient;

    locationClient: LocationClient;
    locationList: WebLocation[];
    locationListSubscription: Subscription;
    location: WebLocation = {};

    webSettingsSubscription: Subscription;
    webSettings: WebSettings;

    contactList: Contact[];
    contactSubscription: Subscription;

    errorMatcher = new CrossFieldErrorMatcher(["required"]);

    constructor(
        private locationService: LocationListService,
        private companyService: CompanyService,
        public dialog: MatDialog,
        private contactService: ContactService,
        private utilityClient: UtilityClient,
        private customerService: CustomerService,
        private sharedService: SharedService,
        public utilityService: UtilityService,
        private errorlog: ErrorLoggingService,
        private sessionDataService: SessionDataService
    ) { }

    ngOnInit(): void
    {
        this.companySubscription = this.companyService.company.subscribe((c) =>
        {
            this.company = c;
        });

        this.webSettingsSubscription = this.companyService.webSettings.subscribe((webSettings) =>
        {
            if (webSettings !== null)
            {
                this.webSettings = webSettings;

                this.restrictDeliveryForPayment =
                    webSettings.RestrictPaymentRequiredDelivery ?? false;
                this.paymentRequiredMessage = webSettings.RestrictPaymentRequiredMessage;
                this.isPriceCalculatorEnabled = webSettings.IsPriceCalculatorEnabled ?? false;
                this.areDeliveryAmountsLockedIn =
                    webSettings.IsPriceCalculatorAmountsLockEnabled ?? false;
            }
        });

        this.customerSubscription = this.customerService.customer.subscribe((customer) =>
        {
            this.customer = customer;
            if (this.customer.AcctStat === 6 || this.customer.AcctStatSameAsCod)
            {
                this.isCODCustomer = true;
            }
        });

        this.isEmployee = this.sessionDataService.IsEmployee;

        this.contactSubscription = this.contactService.contacts.subscribe((c) =>
        {
            this.contactList = c;
        });

        if (this.contactList.length > 0)
        {
            if (this.contactList[0] == null || this.contactList[0].Email === "")
            {
                this.isEditEmailEnabled = true;
                this.defaultEmail = "none available";
            }
            else
            {
                this.isEditEmailEnabled = false;
                this.defaultEmail = this.contactList[0].Email;
                this.contact = this.contactList[0];
            }
        }
        else
        {
            this.isEditEmailEnabled = true;
        }

        this.locationListSubscription = this.locationService.locationList.subscribe((e) =>
        {
            this.locationList = [...e].filter((location) => location.Inactive === false);
            if (this.locationList.length == 1)
            {
                this.defLoc = this.locationList[0];
            }
            if (this.defLoc != null)
            {
                this.dispAlert();
            }
        });
    }

    modelChange(event: any): void
    {
        this.selectedLoc = event;
        this.requestData.TankLevel = null;
        this.dispAlert();
    }

    onTankLevelChange(tankLevel: number)
    {
        this.requestData.TankLevel = tankLevel;
    }

    onErrorPresent(showError: boolean)
    {
        this.isErrorPresentInPriceCalculator = showError;
    }

    showPriceCalculator(): boolean
    {
        return (
            this.isDelivery &&
            this.selectedLoc &&
            this.webSettings.IsPriceCalculatorEnabled
        );
    }

    isCustomerRequiredToPayBeforeDelivery(): boolean
    {
        this.isCODPayBeforeDeliveryEnabled =
            this.webSettings?.IsPaymentRequiredForCODDelivery;
        return (
            (this.customer.AcctStat === 6 || this.customer.AcctStatSameAsCod) &&
            this.isCODPayBeforeDeliveryEnabled &&
            this.isDelivery
        );
    }

    dispAlert(): void
    {
        if (this.defLoc != null)
        {
            this.selectedLoc = this.defLoc;
        }
        const branchCustId = this.customer?.BranchCustId;
        const isDelivery = !!branchCustId && this.isDelivery;
        const isDeliveryCustomer = isDelivery && !this.isEmployee;

        const deliveryRestriction =
            isDeliveryCustomer &&
            this.selectedLoc?.DelTicket === "P" &&
            this.restrictDeliveryForPayment;

        const priceCalculatorRestriction =
            isDeliveryCustomer &&
            this.webSettings?.IsPriceCalculatorEnabled &&
            (this.selectedLoc?.Price == null || this.selectedLoc.Price === 0);

        const isDeliveryRestricted = deliveryRestriction || priceCalculatorRestriction;

        if (isDeliveryRestricted)
        {
            let message: string;

            if (deliveryRestriction && this.paymentRequiredMessage?.length > 0)
            {
                message = this.paymentRequiredMessage;
            }
            else
            {
                message =
                    "Unable to process your delivery request, please call the office at \n Phone:" +
                    this.company.Phone;
            }
            const alertDialog = this.dialog.open(AlertDialogComponent, {
                id: "delivery-request-restricted-alert",
                panelClass: "form-field-less-than-180",
                maxWidth: "600px",
                maxHeight: "500px",
                data: {
                    title: "Alert",
                    message: message,
                    buttonText: "Close",
                },
            });
            alertDialog.afterClosed().subscribe(() =>
            {
                if (this.locationList.length === 1)
                {
                    this.dialog.closeAll();
                }
                else
                {
                    this.requestData.Location = null;
                }
            });
            return;
        }

        if (isDelivery && this.selectedLoc.DelTicket != "P")
        {
            this.emailRequest.RequestType = "Delivery";
            this.fillWebRequest();
            this.customerService
                .pendingDeliveries(this.customer.BranchCustId, this.selectedLoc.Loc)
                .subscribe((b) =>
                {
                    if (b == true)
                    {
                        const dialogRef = this.dialog.open(
                            PendingDeliveryDialogComponent,
                            {
                                minWidth: 400,
                                minHeight: 100,
                                maxWidth: "600px",
                                maxHeight: "500px",
                                data: {
                                    title: "Delivery is Pending",
                                    message:
                                        "We're working on your previously requested delivery for this location. Are you sure you would like to request another one? If you have any questions, please call the office at " +
                                        this.company.Phone,
                                },
                                restoreFocus: true,
                                disableClose: true,
                            }
                        );
                        dialogRef.afterClosed().subscribe((v) =>
                        {
                            if (v)
                                this.utilityService.sendPendingDeliveryEmail(this.emailRequest);
                        });
                        dialogRef.afterClosed().subscribe((v) => { });
                    }
                });
        }

        if (isDeliveryCustomer && this.selectedLoc.DelTicket == "P" && this.restrictDeliveryForPayment)
        {
            const message =
                !SharedService.IsNullOrEmpty(this.paymentRequiredMessage)
                    ? this.paymentRequiredMessage
                    : "Unable to process your delivery request, please call the office at \n Phone:" +
                    this.company.Phone;
            const alertDialog = this.dialog.open(AlertDialogComponent, {
                id: "delivery-request-restricted-alert",
                panelClass: "form-field-less-than-180",
                maxWidth: "600px",
                maxHeight: "500px",
                data: {
                    title: "Alert",
                    message: message,
                    buttonText: "Close",
                },
            });
            alertDialog.afterClosed().subscribe(() =>
            {
                if (this.locationList.length === 1)
                {
                    this.dialog.closeAll();
                }
                else
                {
                    this.requestData.Location = null;
                }
            });
        }
        if (isDelivery && this.selectedLoc.LocationTypeDescription.includes("NF"))
        {
            const message =
                "This location is a non-fuel location. Delivery requests are not accepted for non-fuel locations."
            const alertDialog = this.dialog.open(AlertDialogComponent, {
                id: "delivery-request-non-fuel-alert",
                panelClass: "form-field-less-than-180",
                maxWidth: "600px",
                maxHeight: "500px",
                data: {
                    title: "Request not allowed",
                    message: message,
                    buttonText: "Close",
                },
            });
            alertDialog.afterClosed().subscribe(() =>
            {
                if (this.locationList.length === 1)
                {
                    this.dialog.closeAll();
                }
                else
                {
                    this.requestData.Location = null;
                }
            });
        }

        if (this.customer.BranchCustId != null && !this.isDelivery)
        {
            this.emailRequest.RequestType = "Service";
            this.fillWebRequest();
            this.customerService
                .doesLocationHaveAPendingSWO(this.customer.BranchCustId, this.selectedLoc.Loc)
                .subscribe((b) =>
                {
                    if (b == true)
                    {
                        const dialogRef = this.dialog.open(
                            PendingDeliveryDialogComponent,
                            {
                                minWidth: 400,
                                minHeight: 100,
                                maxWidth: "600px",
                                maxHeight: "500px",
                                data: {
                                    title: "Service is Pending",
                                    message:
                                        "We're working on your previously requested service for this location. Are you sure you would like to request another one? If you have any questions, please call the office at " +
                                        this.company.Phone,
                                },
                                restoreFocus: true,
                                disableClose: true,
                            }
                        );
                        dialogRef.afterClosed().subscribe((v) =>
                        {
                            if (v)
                                this.utilityService.sendPendingDeliveryEmail(this.emailRequest);
                        });
                    }
                });
        }
    }

    fillWebRequest(): void
    {
        this.emailRequest.BranchNo = this.customer.BranchCustId;
        this.emailRequest.CustId = this.customer.CustId;
        this.emailRequest.Date = new Date();
        this.emailRequest.ActionDate = new Date();
        this.emailRequest.Loc = this.selectedLoc.Loc;
        this.emailRequest.Customer = this.customer;
        this.emailRequest.Company = this.company;
        this.emailRequest.Location = this.selectedLoc;
        this.emailRequest.ContactEmail = this.defaultEmail;
        try
        {
            this.emailRequest.Phone = this.contact?.PhoneNumber?.toString() ?? "N/A";
            this.emailRequest.Contact = this.contact?.FirstName;
        }
        catch (error)
        {
            this.errorlog.logError(`Contact is null ${ error }`);
            this.emailRequest.Phone = "N/A";
            this.emailRequest.Contact = "N/A";
        }
        this.emailRequest.Message = null;
    }

    checkFormValidity(form: NgForm)
    {
        if (!form.valid || this.isErrorPresentInPriceCalculator)
        {
            const dialogRefAlert = this.dialog.open(AlertDialogComponent, {
                panelClass: "form-field-less-than-180",
                maxWidth: "600px",
                maxHeight: "500px",
                data: {
                    title: "Alert",
                    message: "Please fully fill out the form in order to submit the request.",
                    buttonText: "Close"
                }
            });
            dialogRefAlert.afterClosed().subscribe(() =>
            {
            });
        }
        else
        {
            this.submitData();
        }
    }

    submitData(): void
    {
        this.showCustHeaderChange.emit(!this.showCustHeader);

        const dialogText: string = "Request Form";
        const processingDialogRef = this.dialog.open(ProcessingDialogComponent, {
            data: dialogText,
            width: "400px",
            maxWidth: "90%",
            restoreFocus: false,
            disableClose: true,
        });

        this.requestData.BranchNo = this.customer.BranchNo;
        this.requestData.CustId = this.customer.CustId;
        this.requestData.Date = new Date();
        this.requestData.ActionDate = new Date();
        this.requestData.Loc = this.selectedLoc.Loc;
        this.requestData.Location = this.selectedLoc;
        this.requestData.Location.LocUsageDesc = this.selectedLoc.LocUsageDesc;
        this.requestData.Location.FullAddress = this.selectedLoc.FullAddress;
        this.requestData.Phone = this.requestData.Phone ?? this.emailRequest.Phone;
        this.requestData.Customer = this.customer;
        this.requestData.Company = this.company;

        if (!this.isEditEmailEnabled)
        {
            this.requestData.ContactEmail = this.defaultEmail;
        }

        let result: boolean;

        if (this.isDelivery)
        {
            this.requestData.RequestType = "Delivery";
            if (this.isCustomerRequiredToPayBeforeDelivery())
            {
                this.messageForCODDelivery =
                    !SharedService.IsNullOrEmpty(this.webSettings.PaymentRequiredForCODDeliveryMessage)
                        ? this.webSettings.PaymentRequiredForCODDeliveryMessage
                        : "A payment is required for delivery. Your request will process after you complete the payment for this delivery.";
                const priceCalculatorTotals =
                    this.priceCalculatorComponent.getPriceCalculatorTotals();
                this.requestData.QuantityTotal = priceCalculatorTotals.QuantityTotal
                    ? parseFloat(priceCalculatorTotals.QuantityTotal.toFixed(0))
                    : null;
                this.requestData.TotalAmount = priceCalculatorTotals.CalculatedTotal
                    ? parseFloat(priceCalculatorTotals.CalculatedTotal.toFixed(2))
                    : null;
                this.requestData.LocationPrice = priceCalculatorTotals.LocationPrice
                    ? parseFloat(priceCalculatorTotals.LocationPrice.toFixed(5))
                    : null;

                const dialogRefMAP = this.dialog.open(MakeAPaymentDialogComponent, {
                    panelClass: "form-field-less-than-180",
                    minHeight: "400px",
                    maxHeight: "90%",
                    maxWidth: "90%",
                    disableClose: true,
                    data: {
                        isPaymentForCODDelivery: true,
                        totalAmount: parseFloat(
                            priceCalculatorTotals.CalculatedTotal.toFixed(2)
                        ),
                    },
                });
                dialogRefMAP.afterClosed().subscribe((_) =>
                {
                    if (this.sharedService.isPaymentForCODMade)
                    {
                        this.utilityClient
                            .writeManualDelivery(this.requestData)
                            .subscribe((r) =>
                            {
                                if (r)
                                {
                                    this.confirmDialog();
                                }
                            }),
                            (error) =>
                            {
                                this.errorDialog(error);
                            }
                    }
                    else 
                    {
                        const dialogRefAlert = this.dialog.open(AlertDialogComponent, {
                            id: "payment-failed-alert",
                            panelClass: "form-field-less-than-180",
                            maxWidth: "600px",
                            maxHeight: "500px",
                            data: {
                                title: "Alert",
                                message:
                                    "Your delivery request was not sent because the payment was not successful.",
                            },
                        });
                        dialogRefAlert.afterClosed().subscribe(_ =>
                        {
                            this.showCustHeaderChange.emit(!this.showCustHeader);
                            this.dialog.closeAll();
                        });
                    }
                });
            }
            else
            {
                if (this.areDeliveryAmountsLockedIn && !this.isCustomerRequiredToPayBeforeDelivery())
                {
                    const priceCalculatorTotals =
                        this.priceCalculatorComponent.getPriceCalculatorTotals();
                    this.requestData.QuantityTotal = priceCalculatorTotals.QuantityTotal
                        ? parseFloat(priceCalculatorTotals.QuantityTotal.toFixed(0))
                        : null;
                    this.requestData.TotalAmount = priceCalculatorTotals.CalculatedTotal
                        ? parseFloat(priceCalculatorTotals.CalculatedTotal.toFixed(2))
                        : null;
                    this.requestData.LocationPrice = priceCalculatorTotals.LocationPrice
                        ? parseFloat(priceCalculatorTotals.LocationPrice.toFixed(5))
                        : null;
                    this.sharedService.isPaymentForCODMade = false;
                }
                this.utilityClient.writeManualDelivery(this.requestData).subscribe(r =>
                {
                    processingDialogRef.close();
                    result = r;
                    if (result) 
                    {
                        this.confirmDialog();
                    }
                    else 
                    {
                        processingDialogRef.close();
                        const dialogRefAlert = this.dialog.open(AlertDialogComponent, {
                            id: "delivery-problem-alert",
                            panelClass: "form-field-less-than-180",
                            maxWidth: "600px",
                            maxHeight: "500px",
                            data: {
                                title: "Alert",
                                message: "There was a problem and your delivery request was not sent. Please try again."
                            }
                        });
                        dialogRefAlert.afterClosed().subscribe(_ =>
                        {
                            this.showCustHeaderChange.emit(!this.showCustHeader);
                        });
                    }
                },
                    (error) =>
                    {
                        processingDialogRef.close();
                        this.errorDialog(error);
                    });
            }
        }
        else
        {
            this.requestData.RequestType = "Service";
            this.utilityClient.writeServiceWorkOrder(this.requestData).subscribe(r => 
            {
                processingDialogRef.close();
                result = r;
                if (result)
                {
                    this.confirmDialog();
                }
                else 
                {
                    processingDialogRef.close();
                    const dialogRefAlert = this.dialog.open(AlertDialogComponent, {
                        id: "service-problem-alert",
                        panelClass: "form-field-less-than-180",
                        maxWidth: "600px",
                        maxHeight: "500px",
                        data: {
                            title: "Alert",
                            message: "There was a problem and your service request was not sent. Please try again."
                        }
                    });
                    dialogRefAlert.afterClosed().subscribe(_ =>
                    {
                        this.showCustHeaderChange.emit(!this.showCustHeader);
                    });
                }

            },
                (error) =>
                {
                    processingDialogRef.close();
                    this.errorDialog(error);
                });
        }
    }

    confirmDialog(): void
    {
        const dialogRef = this.dialog.open(RequestConfirmationDialogComponent, {
            data: {
                request: this.requestData,
                isPaymentMade: this.sharedService.isPaymentForCODMade,
            },
        });
        dialogRef.afterClosed().subscribe(_ =>
        {
            this.dialog.closeAll();
        });
    }

    errorDialog(error): void
    {
        const errorMessage: string = "There was an error submitting your request. \n" + error;

        const dialogRef = this.dialog.open(ErrorDialogComponent, {
            minWidth: "360px",
            data: errorMessage,
        });
        dialogRef.afterClosed().subscribe(_ =>
        {
            this.showCustHeaderChange.emit(!this.showCustHeader);
        });
    }

    ngOnDestroy()
    {
        this.companySubscription?.unsubscribe();
        this.locationListSubscription?.unsubscribe();
        this.contactSubscription?.unsubscribe();
        this.customerSubscription?.unsubscribe();
        this.webSettingsSubscription?.unsubscribe();
    }
}
